import { useState, useEffect } from 'react';
import slackIcon from '@/assets/protocols/menu-slack.svg';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Editor } from '@/components/ui/editor';
import { ACTION_POLICY_TYPE, EMAIL_BODY_TEMPLATE, EMAIL_SUBJECT_TEMPLATE, INSTRUCTION_ACTIONS, PRIVATE_NOTE_TEMPLATE } from '../constants';
import type { InstructionActionType } from '../constants';
import type { SendEmailActionData, PrivateNoteActionData, SendSlackMessageActionData, InstructionActionData } from '../types';
import { cn } from '@/lib/utils';
import { useEscalationPolicy } from '@/hooks/use-escalation-policy';
import { hasPolicy, missingEscalationFields } from '@/modules/escalation-policy/escalation-policy';
import { EscalationPolicyButton } from '@/modules/escalation-policy/escalation-policy-button';

interface ActionComposerProps {
  actionType: InstructionActionType;
  onSave: (data: SendEmailActionData | PrivateNoteActionData | SendSlackMessageActionData) => void;
  onCancel: () => void;
  existingData?: InstructionActionData | null;
}

export const ActionComposer = ({ actionType, onSave, onCancel, existingData }: ActionComposerProps) => {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { data: policy } = useEscalationPolicy();
  const policyType = ACTION_POLICY_TYPE[actionType];
  const [slackChannel, setSlackChannel] = useState('');
  const [slackMessage, setSlackMessage] = useState('');
  // A brand with a policy for this action starts a new one blank, so the policy fills it.
  const startBlank = hasPolicy(policy, policyType);

  // Populate fields when editing existing action
  useEffect(() => {
    if (existingData) {
      if (existingData.type === INSTRUCTION_ACTIONS.SEND_EMAIL) {
        const emailData = existingData as SendEmailActionData;
        setTo(emailData.to || '');
        setSubject(emailData.subject || '');
        setBody(emailData.body || '');
      } else if (existingData.type === INSTRUCTION_ACTIONS.PRIVATE_NOTE) {
        const noteData = existingData as PrivateNoteActionData;
        setNoteContent(noteData.content || '');
      } else if (existingData.type === INSTRUCTION_ACTIONS.SEND_SLACK_MESSAGE) {
        setSlackChannel(existingData.slackChannel || '');
        setSlackMessage(existingData.slackMessage || '');
      }
    } else {
      // Set default templates for new actions
      setTo('');
      if (actionType === INSTRUCTION_ACTIONS.SEND_EMAIL) {
        setSubject(startBlank ? '' : EMAIL_SUBJECT_TEMPLATE);
        setBody(startBlank ? '' : EMAIL_BODY_TEMPLATE);
      } else if (actionType === INSTRUCTION_ACTIONS.PRIVATE_NOTE) {
        setNoteContent(startBlank ? '' : PRIVATE_NOTE_TEMPLATE);
      } else {
        setSubject('');
        setBody('');
        setNoteContent('');
      }
    }
    // Set once per action opened: a policy arriving later must not wipe what the user has typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingData, actionType]);

  const isSendEmail = actionType === INSTRUCTION_ACTIONS.SEND_EMAIL;
  const isPrivateNote = actionType === INSTRUCTION_ACTIONS.PRIVATE_NOTE;
  const isSlack = actionType === INSTRUCTION_ACTIONS.SEND_SLACK_MESSAGE;

  // A blank field is fine when the brand's escalation policy sets it; only one neither sets is refused.
  const refuse = (attrs: Record<string, unknown>) => {
    const missing = missingEscalationFields(policyType, attrs, policy);
    if (missing.length) setError(`Add the ${missing.join(', ')} — the brand policy does not set it`);
    return missing.length > 0;
  };

  const handleSave = () => {
    setError(null);
    if (isSendEmail) {
      if (refuse({ to, subject, body })) return;
      onSave({
        type: 'KIM_SEND_EMAIL',
        to: to.trim(),
        subject: subject.trim(),
        body: body.trim()
      });
    } else if (isPrivateNote) {
      if (refuse({ noteContent })) return;
      onSave({
        type: 'KIM_SEND_PRIVATE_NOTE',
        content: noteContent.trim()
      });
    } else if (isSlack) {
      if (refuse({ slackChannel, slackMessage })) return;
      onSave({
        type: 'KIM_SEND_SLACK_MESSAGE',
        slackChannel: slackChannel.trim().replace(/^#/, ''),
        slackMessage: slackMessage.trim()
      });
    }
  };

  const getActionIcon = () => (isSlack ? slackIcon : '/assets/escalate/kim-action.svg');

  const getActionTitle = () => {
    if (isSendEmail) return 'Send Email';
    if (isPrivateNote) return 'Send Private Note';
    if (isSlack) return 'Send Slack Message';
    return '';
  };

  return (
    <div className='flex flex-col h-full'>
      {/* Sub-header with action name and logo */}
      <div className='flex items-center justify-between p-4'>
        <div className='flex items-center gap-2'>
          <img src={getActionIcon()} alt={getActionTitle()} className='w-5 h-5' />
          <h3 className='text-sm font-semibold'>{getActionTitle()}</h3>
        </div>
        <div className='flex items-center gap-2'>
          <EscalationPolicyButton type={policyType} />
          <Button type='button' variant='outline' size='sm' className='text-xs' onClick={onCancel}>
            Cancel
          </Button>
          <Button type='button' size='sm' className='text-xs' onClick={handleSave}>
            Save Action
          </Button>
        </div>
      </div>

      {error ? (
        <p role='alert' className='px-4 text-xs text-destructive'>
          {error}
        </p>
      ) : null}

      {/* Composer Content */}
      <div className='flex-1 overflow-auto p-4'>
        {isSendEmail && (
          <div className='flex flex-col flex-1 h-full'>
            {/* To Field */}
            <div className='relative'>
              <span className='absolute left-3 top-1/2 -translate-y-1/2 text-xs text-primary/80'>To</span>
              <Input
                id='email-to'
                type='email'
                placeholder='Search People'
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className='pl-9 py-5 text-xs rounded-none focus-visible:ring-0 focus-visible:border-border shadow-none w-full border-x-0 border-y'
              />
            </div>

            {/* Subject Field */}
            <div>
              <Input
                id='email-subject'
                placeholder='Subject'
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className='py-5 text-xs rounded-none focus-visible:ring-0 focus-visible:border-border shadow-none w-full border-x-0 border-t-0 border-b'
              />
            </div>

            {/* Body Editor */}
            <div className='flex-1 flex flex-col'>
              <Editor
                value={body}
                onChange={setBody}
                placeholder='Write your message here...'
                showToolbar={true}
                className='flex-1'
                fontSize='12px'
              />
            </div>
          </div>
        )}

        {isPrivateNote && (
          <div className='flex flex-col flex-1 h-full'>
            {/* Private Note Editor with amber background */}
            <div className={cn('border rounded-2xl overflow-hidden bg-amber-100 flex-1 flex flex-col', '')}>
              <Editor
                value={noteContent}
                onChange={setNoteContent}
                placeholder='Start Typing...'
                showToolbar={true}
                className='flex-1 bg-amber-100'
                fontSize='12px'
              />
            </div>
          </div>
        )}

        {isSlack && (
          <div className='flex flex-col flex-1 h-full gap-2'>
            <Input
              aria-label='Slack channel'
              placeholder='Channel, e.g. support-escalations — blank uses the brand policy'
              value={slackChannel}
              onChange={(e) => setSlackChannel(e.target.value)}
              className='py-5 text-xs rounded-none focus-visible:ring-0 focus-visible:border-border shadow-none w-full border-x-0 border-t-0 border-b'
            />
            <Textarea
              aria-label='Slack message'
              placeholder='Write the message… {{TICKET_URL}} and {{CONTEXT}} are filled in. Blank uses the brand policy.'
              value={slackMessage}
              onChange={(e) => setSlackMessage(e.target.value)}
              className='flex-1 min-h-[160px] text-xs resize-none'
            />
          </div>
        )}
      </div>
    </div>
  );
};
